<template>
  <div
    :class="appendCls"
    @click.stop="toggleMenu"
    v-clickoutside="handleClose">
    <zm-scrollbar
      ref="wrapper"
      class="zm-select-scrollbar-fix"
      :wrap-style="inputWrapStyl">
      <div
        ref="reference"
        class="zm-select-input"
        :style="inputStyl"
        :class="{
          'has-slot': $slots.prefix,
          'zm-select--show-label': (selectedLabel || selected.length) && isInlineLabel
        }"
      >
        <label v-if="(selectedLabel || selected.length) && isInlineLabel" class="zm-select__label" :for="labelFor">{{ inlineLabelText }}</label>
        <div class="zm-select-prefix" v-if="$slots.prefix">
          <slot name="prefix"></slot>
        </div>
        <span
          ref="referenceInner"
          role="button"
          class="zm-select-span__inner"
          :class="{'is-placeholder': selectedLabel===''}"
          :tabindex="selectDisabled ? -1 : 0"
          :id="targetId"
          :disabled="selectDisabled"
          :aria-expanded="(visible).toString()"
          aria-haspopup="listbox"
          :aria-controls="listId"
          :aria-owns="listId"
          :aria-required="isRequired"
          :aria-disabled="selectDisabled"
          :aria-label="(label ? (label + ',') : '') + (selectedLabel || currentPlaceholder)"
          :aria-describedby="labelBy"
          @focus="handleFocus"
          @blur="handleBlur"
          @keydown="handleBtnKeyDown"
        >
         {{selectedLabel || (isInlineLabel ? inlineLabelText : currentPlaceholder)}}
      </span>
        <div
          class="zm-select__tags"
          v-if="multiple"
          ref="tags"
          :style="{ 'max-width': inputWidth - 25 + 'px' }">
          <span v-if="collapseTags && selected.length">
            <zm-tag
              :closable="!selectDisabled"
              class="zm-tag-fix"
              :size="collapseTagSize"
              :hit="selected[0].hitState"
              :delete-text="t('tag.delete') + ' ' + selected[0].currentLabel"
              type="info"
              @close="deleteTag($event, selected[0])"
              disable-transitions>
              <span class="zm-select__tags-text">{{ selected[0].currentLabel }}
              </span>
            </zm-tag>
            <zm-tag
              class="zm-tag-fix"
              v-if="selected.length > 1"
              :closable="false"
              :size="collapseTagSize"
              type="info"
              disable-transitions>
            <span class="zm-select__tags-text">+ {{ selected.length - 1 }}
            </span>
            </zm-tag>
          </span>
          <div v-if="!collapseTags">
            <zm-tag
              v-for="item in selected"
              :key="getValueKey(item)"
              :delete-text="t('tag.delete') + ' '+ item.currentLabel"
              :closable="!selectDisabled"
              :size="collapseTagSize"
              :hit="item.hitState"
              type="info"
              class="zm-tag-fix"
              @close="deleteTag($event, item)"
              disable-transitions>
              <span class="zm-select__tags-text">{{ item.currentLabel }}</span>
            </zm-tag>
          </div>
        </div>
        <button
          class="zm-select-clear"
          v-show="showClear"
          type="button"
          @click.stop="clearSelect"
          @keydonw.enter="clearSelect"
          aria-label="Clear Select"
        >
          <i :class="['zm-select__caret', 'zm-input__icon', 'zm-icon-error','is-show-close']"></i>
        </button>
        <button
          tabindex="-1"
          class="zm-select-toggle"
          type="button"
          aria-hidden="true"
          @click.stop="handleIconClick"
          :aria-label="toggleText">
          <i :class="['zm-select__caret', 'zm-input__icon', 'zm-icon-up']"></i>
        </button>
      </div>
    </zm-scrollbar>
    <transition
      name="zm-zoom-in-top"
      @after-enter="handleMenuEnter"
      @after-leave="handleMenuLeave"
      :duration="duration"
    >
      <zm-select-dropdown
        ref="popper"
        :class="{'zm-select-menu-width-limit': widthLimit}"
        :append-to-body="popperAppendToBody"
        :width-limit="widthLimit"
        :popper-options="popperOptions"
        v-show="visible && emptyText!==false">
        <zm-scrollbar
          wrap-class="zm-select-dropdown__wrap"
          :wrap-style="wrapStyl"
          ref="scrollbar"
          :class="{ 'is-empty': hasEmptyCls }"
        >
          <zm-checkbox-list
            v-if="multiple"
            ref="checkboxList"
            v-model="checkListVal"
            :items="checkboxItems"
            :column-count="1"
            :span="24"
            role="application"
            :limit="multipleLimit"
            :id="listId"
            @change="onCheckListChange"
            @keydown="handleCheckboxListKeyDown"
          >
          </zm-checkbox-list>
          <dl
            role="listbox"
            tabindex="-1"
            :aria-label="listLabel"
            :aria-labelledby="listLabelBy"
            :id="listId"
            class="zm-select-dropdown__list"
            v-else
            v-show="shouldShowOptions"
            ref="list"
            :aria-activedescendant="visible ? activedescendant: null"
            @keydown="handleListKeyDown"
          >
            <slot></slot>
          </dl>
        </zm-scrollbar>
        <template v-if="emptyText">
          <slot name="empty" v-if="$slots.empty"></slot>
          <p class="zm-select-dropdown__empty" v-else>
            {{ emptyText }}
          </p>
        </template>
      </zm-select-dropdown>
    </transition>
  </div>
</template>

<script>
  import SelectMixin from './select-mixin';
  import { valueEquals } from '../../utils/util';
  import AriaUtils from '../../utils/aria-utils';

  const sizeMap = {
    'xLarge': 46,
    'large': 38, // 加上2像素的边框厚度后为40px
    'small': 30
  };

  export default {
    name: 'normal-select',
    componentName: 'normal-select',
    mixins: [SelectMixin],
    props: {
      checkboxOptions: {
        type: Array,
        default() {
          return [];
        }
      }
    },
    data() {
      return {
        checkListVal: this.value
      };
    },
    computed: {
      hasEmptyCls() {
        if (this.multiple) {
          return this.checkboxOptions.length === 0;
        } else {
          return this.optionsCount === 0;
        }
      },
      emptyText() {
        if ((this.multiple && this.checkboxOptions.length) === 0 || (!this.multiple && this.options.length === 0)) {
          return this.noDataText || this.t('select.noData');
        }
        return null;
      },
      checkboxItems() {
        const items = [];
        this.checkboxOptions.forEach((option) => {
          items.push({
            label: option.value,
            displayName: option.label,
            disabled: option.disabled,
            ariaLabel: option.ariaLabel
          });
        });
        return items;
      }
    },
    watch: {
      value(newVal, oldVal) {
        if (this.multiple) {
          if (newVal.length > 0) {
            this.currentPlaceholder = '';
          } else {
            this.currentPlaceholder = this.cachedPlaceHolder;
          }
          this.checkListVal = newVal;
        }
        this.setSelected();
        if (!valueEquals(newVal, oldVal)) {
          this.dispatch('ZmFormItem', 'el.form.change', newVal);
        }
      },
      visible(newVal) {
        this.resetHoverIndex();
        if (newVal) {
          this.handleIconShow();
          this.broadcast('ZmSelectDropdown', 'updatePopper');
          if (!this.multiple && this.selected) {
            this.selectedLabel = this.selected.currentLabel;
          }
          // wait for the list show, for AX
          setTimeout(() => {
            this.isSilentBlur = true;
            this.focusList();
          }, 100);
        } else {
          this.handleIconHide();
          this.broadcast('ZmSelectDropdown', 'destroyPopper');
          this.$emit('blur');
          this.dispatch('ZmFormItem', 'el.form.blur');
        }
        this.$emit('visible-change', newVal);
      },
      options() {
        if (this.selectOptions && this.sortingOptions) {
          return;
        }
        this.resetHoverIndex();
        this.setSelected();
        this.$nextTick(() => {
          this.broadcast('ZmSelectDropdown', 'updatePopper');
        });
      },
      checkboxOptions() {
        this.setSelected();
        this.$nextTick(() => {
          this.broadcast('ZmSelectDropdown', 'updatePopper');
        });
      }
    },
    methods: {
      focus() {
        this.$refs.referenceInner && this.$refs.referenceInner.focus();
      },
      blur() {
        this.$refs.referenceInner && this.$refs.referenceInner.blur();
      },
      handleFocus(event) {
        this.isInputFocus = true;
        this.$emit('focus', event);
      },
      resetInputHeight() {
        if (!this.multiple) {
          return;
        }
        this.$nextTick(() => {
          const sizeInMap = sizeMap[this.selectSize] || 30;
          this.$refs.referenceInner.style.height = this.selected.length === 0
            ? sizeInMap + 'px'
            : 0;
          if (this.visible) {
            this.broadcast('ZmSelectDropdown', 'updatePopper');
          }
        });
      },
      onCheckListChange(list) {
        this.$emit('input', list);
        this.emitChange(list);
      },
      setSelected() {
        if (this.multiple) {
          const result = [];
          if (Array.isArray(this.value)) {
            this.value.forEach(value => {
              const val = { value, currentLabel: this.getLabelByVal(value) };
              result.push(val);
            });
          }
          this.selected = result;
          this.resetInputHeight();
          return;
        }
        const option = this.getOption(this.value);
        this.selectedLabel = typeof this.filterLabel === 'function' ? this.filterLabel(option.currentLabel, this.value) : option.currentLabel;
        this.selected = option;
      },
      getLabelByVal(val) {
        for (let i = 0; i < this.checkboxOptions.length; i++) {
          const option = this.checkboxOptions[i];
          if (option.value === val) {
            return option.label;
          }
        }
      },
      handleBtnKeyDown(event) {
        if (this.selectDisabled) {
          return;
        }
        const keyCode = event.keyCode;
        const keys = AriaUtils.keys;
        switch (keyCode) {
          case keys.enter:
          case keys.space:
          case keys.down:
            this.visible = true;
            event.preventDefault();
            event.stopPropagation();
            break;
        }
      },
      focusList() {
        if (this.multiple) {
          this.$refs.checkboxList.$el.focus();
        } else {
          this.$refs.list.focus();
        }
      },
      handleListKeyDown(event) {
        if (this.selectDisabled) {
          return;
        }
        const keyCode = event.keyCode;
        const keys = AriaUtils.keys;
        switch (keyCode) {
          case keys.enter:
            this.selectOption();
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.esc:
            this.handleEsc();
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.down:
            if (!this.multiple) {
              this.navigateOptions('next');
            }
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.up:
            this.navigateOptions('prev');
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.home:
            this.navigateOptions('home');
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.end:
            this.navigateOptions('end');
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.tab:
            this.handleTab(event);
            break;
        }
      },
      handleCheckboxListKeyDown(event) {
        const keyCode = event.keyCode;
        const keys = AriaUtils.keys;

        switch (keyCode) {
          case keys.esc:
            this.handleEsc();
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.tab:
            this.visible = false;
            event.preventDefault();
            this.$nextTick(() => {
              this.focus();
            });
            break;
        }
      }
    }
  };
</script>

